import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';
import ViewContainer from 'components/utils/ViewContainer';
import { Card, CardBody, CardHeader, Col, Row, Input } from 'reactstrap';
import SyncButton from 'components/utils/SyncButton';
import { compose } from 'recompose';
import { withApiData, ApiDataBinding } from 'react-api-data';
import { ApiResponse, MailingList } from 'constants/api';
import { get } from 'lodash';
import RequestStatusRenderer from 'components/utils/RequestStatusRenderer';
import Callout from 'components/utils/Callout';
import MailingListAddModal from 'components/modals/MailingListAddModal';
import MailingListsList from 'components/cards/MailingListsList';

interface Props {
    mailingListsBinding: ApiDataBinding<ApiResponse<{ mailing_lists: MailingList[] }>>;
}

const enhance = compose<Props, {}>(
    withApiData({
        mailingListsBinding: 'getMailingLists',
    })
);

function AmsIxCalloutContent() {
    const { t } = useTranslation();

    return (
        <React.Fragment>
            <p>{t('mailingLists.amsixCallout.calloutParagraph1')}</p>
            <ul>
                <li>{t('mailingLists.amsixCallout.bulletPoint1')}</li>
                <li>{t('mailingLists.amsixCallout.bulletPoint2')}</li>
            </ul>
            <p>{t('mailingLists.amsixCallout.calloutParagraph2')}</p>
            <div className="h5">{t('mailingLists.autoresponders.title')}</div>
            <p style={{ whiteSpace: 'pre-wrap' }}>{t('mailingLists.amsixCallout.disclaimerParagraph1')}</p>
            <ul>
                <li>{t('mailingLists.autoresponders.outOfOfficeAssistants')}</li>
                <li>{t('mailingLists.autoresponders.spamFilters')}</li>
                <li>{t('mailingLists.autoresponders.ticketingSystems')}</li>
                <li>{t('mailingLists.autoresponders.grayListing')}</li>
            </ul>
            <p>{t('mailingLists.amsixCallout.disclaimerParagraph2')}</p>
            <p>{t('mailingLists.amsixCallout.disclaimerParagraph3')}</p>
        </React.Fragment>
    );
}

function PoweredByIxaasCalloutContnent({ exchangeName }: { exchangeName: string }) {
    const { t } = useTranslation();

    return (
        <React.Fragment>
            <p style={{ whiteSpace: 'pre-wrap' }}>
                {t('mailingLists.poweredByIxaasCallout.calloutParagraph1', { exchangeName })}
            </p>
            <p>{t('mailingLists.poweredByIxaasCallout.calloutParagraph2')}</p>
            <div className="h5">{t('mailingLists.autoresponders.title')}</div>
            <p style={{ whiteSpace: 'pre-wrap' }}>
                {t('mailingLists.poweredByIxaasCallout.disclaimerParagraph1', { exchangeName })}
            </p>
            <ul>
                <li>{t('mailingLists.autoresponders.outOfOfficeAssistants')}</li>
                <li>{t('mailingLists.autoresponders.spamFilters')}</li>
                <li>{t('mailingLists.autoresponders.ticketingSystems')}</li>
                <li>{t('mailingLists.autoresponders.grayListing')}</li>
            </ul>
            <p>{t('mailingLists.poweredByIxaasCallout.disclaimerParagraph2', { exchangeName })}</p>
            <p>{t('mailingLists.poweredByIxaasCallout.disclaimerParagraph3')}</p>
        </React.Fragment>
    );
}

function MailingListsView(props: Props) {
    const { t } = useTranslation();
    const { mailingListsBinding } = props;
    const [searchTerm, setSearchTerm] = useState('');
    const exchangeName = process.env.REACT_APP_EXCHANGE_NAME!;

    return (
        <>
            <Helmet title={t('mailingLists.pageTitle', { exchangeName })} />
            <ViewContainer>
                <h2>{t('mailingLists.pageTitle', { exchangeName })}</h2>
                <Callout className="bd-callout-warning">
                    {process.env.REACT_APP_IS_POWERED_BY_IXAAS === 'true' ? (
                        <PoweredByIxaasCalloutContnent exchangeName={exchangeName} />
                    ) : (
                        <AmsIxCalloutContent />
                    )}
                </Callout>
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center">
                                <h2>{t('mailingLists.tableHeading')}</h2>
                                <SyncButton
                                    data-test="mailing-lists-refresh-btn"
                                    className="ml-2"
                                    title={t('mailingLists.refreshButton')}
                                    onClick={mailingListsBinding.invalidateCache}
                                />
                            </div>
                            <MailingListAddModal />
                        </div>
                        <hr />
                        <Card className="card-accent-primary">
                            <CardHeader>
                                <Row className="justify-content-between">
                                    <Col xl={3}>
                                        <Input
                                            data-test="search-term-control"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="form-control"
                                            placeholder={t('table.searchInputPlaceholder')}
                                        />
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <RequestStatusRenderer
                                    request={mailingListsBinding.request}
                                    failedMessage={t('notification.unexpectedError')}
                                    success={() => (
                                        <MailingListsList
                                            mailingLists={get(mailingListsBinding, 'data.data.mailing_lists', [])}
                                            searchTerm={searchTerm}
                                        />
                                    )}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </ViewContainer>
        </>
    );
}

export default enhance(MailingListsView);
